import { useIntl } from "react-intl";
import { css } from "emotion";
import { Stack } from "@certa/catalyst/layouts/Stack";
import {
  TypographyColors,
  TypographyVariants,
  Typography
} from "@certa/catalyst/components/Typography";
import type { FieldAnswer, FieldAnswerExtraJSONRecallAI } from "@certa/types";
import { FieldSubmittedBy } from "../FieldSubmittedBy/FieldSubmittedBy";
import { truncateString } from "@certa/common/utils/string";
import { createFilePreviewUrl } from "@certa/filepreviewer/src/FilePreviewerPage";
import { Link } from "react-router";
import { getFileType } from "@certa/common/utils/file";

const SECTION_TEXT_MAX_LENGTH = 150;

const JUSTIFICATION_MAX_LENGTH = 1500;
const TOOLTIP_MAX_HEIGHT = "20rem";

export type UserUploadFieldStatusPopoverProps = Pick<
  FieldAnswerExtraJSONRecallAI,
  "citations" | "justification"
> & {
  answer: Pick<FieldAnswer, "submittedBy" | "submittedAt" | "extraJSON"> | null;
};

export const UserUploadFieldStatusPopover = ({
  citations,
  justification,
  answer
}: UserUploadFieldStatusPopoverProps) => {
  const intl = useIntl();
  const isAssistedFromPublicInfo =
    answer?.extraJSON.recall_ai?.assisted_by_ai === "from_public_info";
  const source = intl.formatMessage({
    id: "common.source",
    defaultMessage: "Source"
  });
  const sectionText = intl.formatMessage({
    id: "common.section",
    defaultMessage: "Section"
  });
  const openSourceLabel = (sourceName: string) =>
    intl.formatMessage(
      {
        id: "common.openSource",
        defaultMessage: "Open source: {sourceName}"
      },
      { sourceName }
    );

  const hasJustification = !!justification;
  return (
    <Stack
      gap="16"
      padding="16"
      width="20rem"
      dangerouslySetClassName={tooltipMaxHeightStyle}
    >
      <Typography
        variant={TypographyVariants.LABEL_SM_BOLD}
        color={TypographyColors.NEUTRAL_700}
      >
        <FieldSubmittedBy
          hasAnswered={true}
          answer={answer}
          isAnsweredByCertaAssist={true}
        />
      </Typography>
      {hasJustification ? (
        <Typography
          variant={TypographyVariants.LABEL_SM}
          color={TypographyColors.NEUTRAL_700}
        >
          {truncateString(justification, JUSTIFICATION_MAX_LENGTH)}
        </Typography>
      ) : null}
      {citations?.length
        ? citations.map((citation, index) => {
            const fileType = getFileType(citation.label);
            const isPdf = fileType === "pdf";
            const linkTo =
              isAssistedFromPublicInfo || !isPdf
                ? citation.url
                : createFilePreviewUrl({
                    url: citation.url,
                    fileName: citation.label,
                    highlightedText: citation.section_text,
                    filePreviewDataSource: "mimirV1"
                  });
            return (
              <Stack key={index} gap="8">
                <Stack direction="horizontal" gap="4">
                  <Typography
                    variant={TypographyVariants.LABEL_SM}
                    color={TypographyColors.NEUTRAL_800}
                    className={minWidthStyle}
                  >
                    {source}:
                  </Typography>
                  {isAssistedFromPublicInfo ? (
                    <a
                      aria-label={openSourceLabel(citation.label)}
                      target="_blank"
                      href={linkTo}
                      rel="noopener noreferrer"
                      className={ellipsisStyle}
                    >
                      {citation.label}
                    </a>
                  ) : (
                    <Link
                      aria-label={openSourceLabel(citation.label)}
                      target="_blank"
                      to={linkTo}
                      className={ellipsisStyle}
                    >
                      {citation.label}
                    </Link>
                  )}
                </Stack>
                {citation.section_name && citation.section_text ? (
                  <div>
                    <Stack direction="horizontal" gap="4">
                      <Typography
                        variant={TypographyVariants.LABEL_SM}
                        color={TypographyColors.NEUTRAL_800}
                        className={minWidthStyle}
                      >
                        {sectionText}:
                      </Typography>
                      <Typography
                        variant={TypographyVariants.LABEL_SM}
                        color={TypographyColors.NEUTRAL_800}
                        className={ellipsisStyle}
                      >
                        {citation.section_name}
                      </Typography>
                    </Stack>
                    <Typography
                      as="p"
                      variant={TypographyVariants.LABEL_SM}
                      color={TypographyColors.NEUTRAL_700}
                    >
                      {truncateString(
                        citation.section_text,
                        SECTION_TEXT_MAX_LENGTH
                      )}
                    </Typography>
                  </div>
                ) : null}
              </Stack>
            );
          })
        : null}
    </Stack>
  );
};

const ellipsisStyle = css({
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
});

const minWidthStyle = css({
  minWidth: "max-content"
});

const tooltipMaxHeightStyle = css({
  maxHeight: TOOLTIP_MAX_HEIGHT,
  overflowY: "auto"
});
